import { motion } from "framer-motion";
import { Check } from "lucide-react";

const EASE = [0.83, 0, 0.17, 1];

const credentials = [
  "Over 20 Years in the Disability & Community Services Sector",
  "Founder & Clinical Director · You Are The Focus Community Care",
  "Academic Foundation in Psychology & Behaviour Management",
  "Licensed Real Estate Agent",
  "Deep Understanding of Disability Housing Pathways",
  "Strategic Relationship Builder & Trusted Industry Connector",
];

export default function WhyFatima() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative bg-plum py-28 md:py-40 border-t border-gold/10"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
        {/* Left: portrait frame */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="lg:col-span-5 lg:sticky lg:top-32"
        >
          <div className="relative aspect-[4/5] w-full portrait-frame border border-gold/25 overflow-hidden">
            <img
              src="/fatima-portrait.jpg"
              alt="Fatima Abdallah portrait"
              className="absolute inset-0 w-full h-full object-cover object-[center_25%] image-portrait"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-plum/5 via-transparent to-plum/60 pointer-events-none" />
            <div className="absolute inset-4 border border-gold/25 pointer-events-none" />
            <span className="absolute top-0 left-0 w-6 h-px bg-gold" />
            <span className="absolute top-0 left-0 w-px h-6 bg-gold" />
            <span className="absolute bottom-0 right-0 w-6 h-px bg-gold" />
            <span className="absolute bottom-0 right-0 w-px h-6 bg-gold" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-cream/85">
              <span>Fatima Abdallah</span>
              <span className="text-gold">FYA</span>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-cream/60">
            <span>Founder & Principal</span>
            <span className="text-gold/80">FYA Property Group</span>
          </div>
        </motion.div>

        {/* Right: content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 text-gold text-[11px] tracking-[0.35em] uppercase">
            <span className="w-8 h-px bg-gold" />
            Why Work With Fatima
          </div>
          <h2 className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
            Two worlds. <span className="italic text-gold">One purpose.</span>
            <br />
            Stronger outcomes.
          </h2>

          <div className="mt-10 space-y-6 max-w-2xl">
            <p className="text-cream/85 text-base md:text-lg leading-relaxed text-justify hyphens-auto">
              Backed by an academic foundation in Psychology and Behaviour
              Management, together with over 20 years of experience across the
              Disability and Community Services Sector, I have developed a
              deep understanding of human behaviour, housing pathways and the
              collaborative partnerships required to create meaningful,
              person-centred outcomes.
            </p>
            <p className="text-cream/85 text-base md:text-lg leading-relaxed text-justify hyphens-auto">
              As the{" "}
              <a
                href="https://www.youarethefocuscommunitycare.com.au/"
                target="_blank"
                rel="noreferrer"
                data-testid="yatf-link-bio"
                className="text-cream underline underline-offset-4 decoration-cream/30 hover:decoration-gold hover:text-gold transition-colors duration-500"
              >
                Founder and Clinical Director of You Are The Focus Community
                Care
              </a>
              , established in 2019, I have led a multidisciplinary organisation
              delivering Behaviour Support, Allied Health and Therapeutic
              Services that empower individuals and families to achieve
              meaningful, long-term outcomes. Before moving into leadership, I
              worked directly as a clinician, providing me with firsthand
              insight into the challenges individuals and families face, and
              the importance of practical, person-centred solutions.
            </p>
            <p className="text-cream/85 text-base md:text-lg leading-relaxed text-justify hyphens-auto">
              As a Licensed Real Estate Agent, I bring a unique perspective by
              combining extensive disability sector experience with strategic
              property expertise. Whether assisting investors exploring
              disability housing opportunities, identifying development land,
              or acting as a buyer's agent for individuals, families and
              investors navigating important property decisions, my focus
              remains the same, connecting the right people with the right
              opportunities.
            </p>
            <p className="text-cream/85 text-base md:text-lg leading-relaxed text-justify hyphens-auto">
              Whether you are investing, rebuilding after a major life
              transition, purchasing your next home, or creating new housing
              opportunities, I am committed to helping you move forward with
              confidence.
            </p>
            <p className="text-cream/85 text-base md:text-lg leading-relaxed text-justify hyphens-auto">
              I believe the most successful outcomes are achieved when People,
              Purpose and Property come together. My role is to build trusted
              relationships, identify strategic opportunities and create
              partnerships that deliver lasting value for investors, developers,
              providers, families and the communities they serve.
            </p>

            {/* Signature pullquote */}
            <blockquote
              data-testid="why-pullquote"
              className="mt-10 pl-6 md:pl-8 border-l border-gold/40"
            >
              <span className="block font-serif italic text-gold text-2xl md:text-3xl lg:text-[34px] leading-snug">
                &ldquo;Great opportunities are not just found — they are
                created through the right relationships.&rdquo;
              </span>
            </blockquote>
          </div>

          {/* Credentials */}
          <div className="mt-14">
            <div className="flex items-center gap-3 text-gold text-[10px] tracking-[0.35em] uppercase mb-6">
              <span className="w-6 h-px bg-gold" />
              Credentials
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
              {credentials.map((c, i) => (
                <motion.li
                  key={c}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.7,
                    ease: EASE,
                    delay: 0.05 * i,
                  }}
                  className="flex items-start gap-3 text-cream/85 text-sm md:text-[15px] leading-relaxed border-b border-gold/10 pb-4"
                  data-testid={`credential-${i + 1}`}
                >
                  <Check
                    size={16}
                    className="text-gold mt-1 shrink-0"
                    strokeWidth={1.5}
                  />
                  <span>{c}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Signature */}
          <div className="mt-14 flex items-center gap-6">
            <span className="font-serif italic text-gold text-3xl md:text-4xl">
              Fatima Abdallah
            </span>
            <span className="text-cream/50 text-[10px] tracking-[0.35em] uppercase">
              Strategic Property & Disability Housing Advisor
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
